// 参加ユーザー一覧（アバター付き）。PO Board の epic-presence-badges に近い構造。
// アバター画像が無い／取得失敗した場合はイニシャル表示でフォールバックする。
// アバターをクリックすると、その参加者の表示画面を追尾する「フォーカスモード」を開始する。

import { getRemoteAvatarAllowedHttpsOrigins } from "../auth/avatar-image-url";
import { UserAvatar } from "./dashboard-shell/user-avatar";

interface Viewer {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarImageUrl?: string | undefined;
  readonly color: string;
}

interface Props {
  readonly viewers: readonly Viewer[];
  readonly maxVisible?: number | undefined;
  /** アバターのクリックで起動するフォーカスモード切り替えハンドラ。 */
  readonly onFocusViewer?: ((userId: string) => void) | undefined;
  /** 現在フォーカス中の userId（無ければ null）。アバターの選択スタイルを切り替えるのに使う。 */
  readonly focusedUserId?: string | null | undefined;
  /**
   * 現在のログインユーザー ID。自分自身のアバターはフォーカス対象として
   * 意味が無いため、クリック不可（装飾のみ）にする。
   */
  readonly currentUserId?: string | undefined;
}

export function PresenceBadges({
  viewers,
  maxVisible = 4,
  onFocusViewer,
  focusedUserId,
  currentUserId,
}: Props): JSX.Element {
  const visible = viewers.slice(0, maxVisible);
  const overflow = viewers.length - visible.length;

  return (
    <div
      role="list"
      aria-label="参加ユーザー一覧"
      style={{
        display: "flex",
        padding: "4px",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: "9999px",
        boxShadow: "var(--shadow-1)",
      }}
    >
      {visible.length === 0 ? (
        <span style={{ padding: "4px 12px", fontSize: "12px", color: "var(--color-text-muted)" }}>
          自分のみ
        </span>
      ) : (
        visible.map((v, i) => (
          <PresenceAvatar
            key={v.userId}
            viewer={v}
            index={i}
            // 自分自身のアバターはフォーカス対象として無意味なため、onFocus を渡さず装飾のみにする。
            onFocus={v.userId === currentUserId ? undefined : onFocusViewer}
            focused={focusedUserId === v.userId}
            isSelf={v.userId === currentUserId}
          />
        ))
      )}
      {overflow > 0 ? (
        <div
          aria-label={`さらに ${overflow} 人`}
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: "var(--color-surface-muted)",
            display: "grid",
            placeItems: "center",
            fontSize: "11px",
            marginLeft: "-8px",
          }}
        >
          +{overflow}
        </div>
      ) : null}
    </div>
  );
}

function PresenceAvatar({
  viewer,
  index,
  onFocus,
  focused,
  isSelf,
}: {
  readonly viewer: Viewer;
  readonly index: number;
  readonly onFocus?: ((userId: string) => void) | undefined;
  readonly focused: boolean;
  readonly isSelf?: boolean | undefined;
}): JSX.Element {
  // クリックで対象ユーザーへのフォーカスモードを開始/解除する。
  // 非対応モード（onFocus 未指定）ではボタン化せず装飾のみとする。
  const interactive = typeof onFocus === "function";
  const sharedStyle = {
    position: "relative" as const,
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: `2px solid ${viewer.color}`,
    marginLeft: index === 0 ? 0 : "-8px",
    background: "var(--color-surface-muted)",
    display: "grid" as const,
    placeItems: "center" as const,
    fontSize: "12px",
    fontWeight: 600,
    color: "var(--color-text)",
    overflow: "hidden" as const,
    boxShadow: focused ? `0 0 0 3px ${viewer.color}` : undefined,
  };

  // 参加者一覧には自分自身も含まれる。リモート（他参加者）由来のアバターのみ https を
  // 信頼済み origin（IdP）へ限定し、任意 https による強制外部リクエスト/トラッキングを防ぐ。
  // 自分自身のアバターは従来どおり任意 https を許容し、自分の正規アイコンの退行を避ける。
  const allowedHttpsOrigins = isSelf ? undefined : getRemoteAvatarAllowedHttpsOrigins();
  const inner = (
    <UserAvatar
      alt={`${viewer.displayName} のアイコン`}
      allowedHttpsOrigins={allowedHttpsOrigins}
      className="presence-user-avatar"
      displayName={viewer.displayName}
      src={viewer.avatarImageUrl}
    />
  );

  if (!interactive) {
    // 自分自身のアバターは「自分」であることを示すツールチップに切り替える。
    const title = isSelf ? `${viewer.displayName}（自分）` : viewer.displayName;
    return (
      <div role="listitem" title={title} style={sharedStyle}>
        {inner}
      </div>
    );
  }

  // listitem ロールは外側のラッパーに持たせ、内側の <button> はネイティブの button
  // semantics（aria-pressed・キーボード操作・読み上げ）を保つ。隣接アバターの重なり
  // (marginLeft) はラッパー側で表現し、button 自体は marginLeft を 0 に上書きする。
  return (
    <span role="listitem" style={{ display: "inline-flex", marginLeft: index === 0 ? 0 : "-8px" }}>
      <button
        type="button"
        title={focused ? `${viewer.displayName} のフォーカスを解除` : `${viewer.displayName} の画面を追尾`}
        aria-label={focused ? `${viewer.displayName} のフォーカスを解除` : `${viewer.displayName} の画面を追尾`}
        aria-pressed={focused}
        onClick={() => onFocus?.(viewer.userId)}
        style={{
          ...sharedStyle,
          marginLeft: 0,
          padding: 0,
          cursor: "pointer",
        }}
      >
        {inner}
      </button>
    </span>
  );
}
